import { useEffect,useMemo,useState } from "react";
import {
  Box,CircularProgress,FormControl,MenuItem,Select,Table,TableBody,
  TableCell,TableContainer,TableHead,TableRow,Typography
} from "@mui/material";
import { useAppTheme } from "../../context/ThemeContext";
import { useAlert } from "../../context/AlertContext";

const readResponse=async response=>{
  const contentType=response.headers.get("content-type")||"";

  if(contentType.includes("application/json")){
    return response.json();
  }

  if(!response.ok){
    throw new Error("El servidor no respondió correctamente.");
  }

  return {};
};

const formatDate=value=>{
  if(!value) return "—";

  const parts=value.split("-");

  if(parts.length!==3){
    return value;
  }

  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const formatType=value=>{
  if(value==="LEAGUE") return "Todos contra todos";
  if(value==="GROUPS") return "Fase de grupos";
  if(value==="KNOCKOUT") return "Eliminación directa";
  return value||"—";
};

export default function AdminTournaments(){
  const {colors,typography,shape}=useAppTheme();
  const {showError}=useAlert();

  const [loading,setLoading]=useState(true);
  const [data,setData]=useState(null);
  const [categorySlug,setCategorySlug]=useState("");

  const loadTournaments=async()=>{
    try{
      setLoading(true);

      const response=await fetch(
        "/tournaments/delegate/",
        {credentials:"include"}
      );

      const result=await readResponse(response);

      if(!response.ok){
        throw new Error(
          result.error||
          result.detail||
          "No se pudieron cargar los torneos."
        );
      }

      setData(result);

      const resultCategories=Array.isArray(result.categories)
        ?result.categories
        :[];

      setCategorySlug(current=>{
        const exists=resultCategories.some(
          category=>category.slug===current
        );

        if(exists){
          return current;
        }

        return resultCategories[0]?.slug||"";
      });
    }catch(error){
      setData(null);
      setCategorySlug("");
      showError(
        error.message||
        "No se pudieron cargar los torneos."
      );
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    loadTournaments();
  },[]);

  const categories=useMemo(
    ()=>Array.isArray(data?.categories)
      ?data.categories
      :[],
    [data]
  );

  const selectedCategory=useMemo(
    ()=>categories.find(
      category=>category.slug===categorySlug
    )||null,
    [categories,categorySlug]
  );

  const tournaments=useMemo(
    ()=>Array.isArray(selectedCategory?.tournaments)
      ?selectedCategory.tournaments
      :[],
    [selectedCategory]
  );

  if(loading){
    return (
      <Box
        sx={{
          width:"100%",
          maxWidth:1300,
          mx:"auto",
          py:6,
          display:"flex",
          justifyContent:"center"
        }}
      >
        <CircularProgress
          size={26}
          sx={{color:colors.secondary}}
        />
      </Box>
    );
  }

  return (
    <Box sx={{width:"100%",maxWidth:1300,mx:"auto"}}>
      <Box
        sx={{
          mb:2,
          display:"flex",
          flexDirection:{xs:"column",md:"row"},
          alignItems:{xs:"stretch",md:"flex-end"},
          justifyContent:"space-between",
          gap:1.5
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize:{
                xs:typography.sizes.xl,
                md:typography.sizes.pageTitle
              },
              fontWeight:700,
              color:colors.text
            }}
          >
            Torneos
          </Typography>

          <Typography
            sx={{
              mt:0.3,
              fontSize:typography.sizes.sm,
              color:colors.textSecondary
            }}
          >
            Consulta los torneos, fases, grupos y rivales de tu equipo.
          </Typography>
        </Box>

        <Box
          sx={{
            width:{xs:"100%",sm:230},
            flexShrink:0
          }}
        >
          <Typography
            sx={{
              mb:0.5,
              fontSize:typography.sizes.xs,
              fontWeight:600,
              color:colors.textSecondary
            }}
          >
            Categoría
          </Typography>

          <FormControl
            fullWidth
            size="small"
            disabled={!categories.length}
          >
            <Select
              value={categorySlug}
              onChange={event=>setCategorySlug(event.target.value)}
              displayEmpty
              sx={{
                height:38,
                borderRadius:shape.radius,
                backgroundColor:colors.surface,
                color:colors.text,
                fontSize:typography.sizes.sm,
                "& .MuiOutlinedInput-notchedOutline":{
                  borderColor:colors.border
                },
                "&:hover .MuiOutlinedInput-notchedOutline":{
                  borderColor:colors.secondary
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline":{
                  borderColor:colors.secondary,
                  borderWidth:1
                }
              }}
            >
              {!categories.length&&(
                <MenuItem value="">
                  Sin categorías
                </MenuItem>
              )}

              {categories.map(category=>(
                <MenuItem
                  key={category.slug}
                  value={category.slug}
                  sx={{
                    fontSize:typography.sizes.sm
                  }}
                >
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {!data?.team?(
        <EmptyTable
          text="No tienes un equipo activo asignado."
          colors={colors}
          shape={shape}
        />
      ):!categories.length?(
        <EmptyTable
          text="Tu equipo no se encuentra inscrito en ningún torneo."
          colors={colors}
          shape={shape}
        />
      ):!tournaments.length?(
        <EmptyTable
          text="No existen torneos para esta categoría."
          colors={colors}
          shape={shape}
        />
      ):(
        <Box
          sx={{
            display:"flex",
            flexDirection:"column",
            gap:2
          }}
        >
          {tournaments.map(tournament=>(
            <TournamentBlock
              key={tournament.slug}
              tournament={tournament}
              myTeam={data.team}
              colors={colors}
              typography={typography}
              shape={shape}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function EmptyTable({text,colors,shape}){
  return (
    <TableContainer
      sx={{
        border:`1px solid ${colors.border}`,
        borderRadius:shape.radius,
        backgroundColor:colors.surface,
        boxShadow:"none",
        overflow:"hidden"
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{backgroundColor:colors.tableHeader}}>
            <TableCell
              sx={{
                color:colors.tableHeaderText,
                fontWeight:700
              }}
            >
              Torneos
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          <TableRow>
            <TableCell
              align="center"
              sx={{
                py:4,
                color:colors.textSecondary
              }}
            >
              {text}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function TournamentBlock({tournament,myTeam,colors,typography,shape}){
  const phases=Array.isArray(tournament.phases)
    ?tournament.phases
    :[];

  return (
    <Box>
      <TableContainer
        sx={{
          border:`1px solid ${colors.border}`,
          borderRadius:shape.radius,
          backgroundColor:colors.surface,
          boxShadow:"none",
          overflow:"hidden"
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{backgroundColor:colors.tableHeader}}>
              <TableCell
                sx={{
                  color:colors.tableHeaderText,
                  fontWeight:700
                }}
              >
                Torneo
              </TableCell>

              <TableCell
                sx={{
                  color:colors.tableHeaderText,
                  fontWeight:700
                }}
              >
                Temporada
              </TableCell>

              <TableCell
                sx={{
                  color:colors.tableHeaderText,
                  fontWeight:700
                }}
              >
                Inicio
              </TableCell>

              <TableCell
                sx={{
                  color:colors.tableHeaderText,
                  fontWeight:700
                }}
              >
                Finalización
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight:700,
                  color:colors.text
                }}
              >
                {tournament.name}
              </TableCell>

              <TableCell sx={{color:colors.text}}>
                {tournament.season||"—"}
              </TableCell>

              <TableCell sx={{color:colors.text}}>
                {formatDate(tournament.starts_on)}
              </TableCell>

              <TableCell sx={{color:colors.text}}>
                {formatDate(tournament.ends_on)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {!phases.length?(
        <Box
          sx={{
            mt:1,
            px:1.5,
            py:2,
            border:`1px solid ${colors.border}`,
            borderRadius:shape.radius,
            backgroundColor:colors.surface
          }}
        >
          <Typography
            sx={{
              textAlign:"center",
              fontSize:typography.sizes.sm,
              color:colors.textSecondary
            }}
          >
            Tu equipo todavía no ha sido asignado a una fase de este torneo.
          </Typography>
        </Box>
      ):(
        <Box
          sx={{
            mt:1,
            display:"flex",
            flexDirection:"column",
            gap:1.5
          }}
        >
          {phases.map(phase=>(
            <PhaseBlock
              key={phase.slug}
              phase={phase}
              myTeam={myTeam}
              colors={colors}
              typography={typography}
              shape={shape}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function PhaseBlock({phase,myTeam,colors,typography,shape}){
  const teams=Array.isArray(phase.teams)
    ?phase.teams
    :[];

  const rivals=Array.isArray(phase.rivals)
    ?phase.rivals
    :teams.filter(team=>team.slug!==myTeam?.slug);

  return (
    <Box>
      <TableContainer
        sx={{
          border:`1px solid ${colors.border}`,
          borderRadius:shape.radius,
          backgroundColor:colors.surface,
          boxShadow:"none",
          overflow:"hidden"
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{backgroundColor:colors.tableHeader}}>
              <TableCell
                sx={{
                  color:colors.tableHeaderText,
                  fontWeight:700
                }}
              >
                Fase
              </TableCell>

              <TableCell
                sx={{
                  color:colors.tableHeaderText,
                  fontWeight:700
                }}
              >
                Formato
              </TableCell>

              <TableCell
                sx={{
                  color:colors.tableHeaderText,
                  fontWeight:700
                }}
              >
                Grupo
              </TableCell>

              <TableCell
                align="center"
                sx={{
                  color:colors.tableHeaderText,
                  fontWeight:700
                }}
              >
                Posición inicial
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight:700,
                  color:colors.text
                }}
              >
                {phase.name}
              </TableCell>

              <TableCell sx={{color:colors.text}}>
                {phase.format_display||formatType(phase.format)}
              </TableCell>

              <TableCell sx={{color:colors.text}}>
                {phase.group?.name||"—"}
              </TableCell>

              <TableCell
                align="center"
                sx={{color:colors.text}}
              >
                {phase.seed??"—"}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          mt:1,
          display:"grid",
          gridTemplateColumns:{
            xs:"1fr",
            lg:"minmax(0,1fr) minmax(0,1fr)"
          },
          gap:1
        }}
      >
        <TableContainer
          sx={{
            border:`1px solid ${colors.border}`,
            borderRadius:shape.radius,
            backgroundColor:colors.surface,
            boxShadow:"none",
            overflow:"hidden"
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{backgroundColor:colors.tableHeader}}>
                <TableCell
                  sx={{
                    color:colors.tableHeaderText,
                    fontWeight:700
                  }}
                >
                  {phase.group?.name
                    ?`Equipos de ${phase.group.name}`
                    :"Equipos de la fase"}
                </TableCell>

                <TableCell
                  align="center"
                  sx={{
                    width:110,
                    color:colors.tableHeaderText,
                    fontWeight:700
                  }}
                >
                  Posición
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {!teams.length?(
                <TableRow>
                  <TableCell
                    colSpan={2}
                    align="center"
                    sx={{
                      py:3,
                      color:colors.textSecondary
                    }}
                  >
                    No hay equipos asignados.
                  </TableCell>
                </TableRow>
              ):(
                teams.map(team=>(
                  <TableRow
                    key={team.slug}
                    sx={{
                      backgroundColor:team.is_my_team
                        ?colors.backgroundSoft
                        :"transparent"
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{
                          display:"flex",
                          alignItems:"center",
                          gap:1
                        }}
                      >
                        {team.logo&&(
                          <Box
                            component="img"
                            src={team.logo}
                            alt={team.name}
                            sx={{
                              width:30,
                              height:30,
                              objectFit:"contain",
                              flexShrink:0
                            }}
                          />
                        )}

                        <Box>
                          <Typography
                            sx={{
                              fontSize:typography.sizes.sm,
                              fontWeight:team.is_my_team?700:600,
                              color:colors.text
                            }}
                          >
                            {team.name}
                          </Typography>

                          {team.is_my_team&&(
                            <Typography
                              sx={{
                                mt:0.1,
                                fontSize:typography.sizes.xs,
                                color:colors.secondary
                              }}
                            >
                              Tu equipo
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{color:colors.text}}
                    >
                      {team.seed??"—"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TableContainer
          sx={{
            border:`1px solid ${colors.border}`,
            borderRadius:shape.radius,
            backgroundColor:colors.surface,
            boxShadow:"none",
            overflow:"hidden"
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{backgroundColor:colors.tableHeader}}>
                <TableCell
                  sx={{
                    color:colors.tableHeaderText,
                    fontWeight:700
                  }}
                >
                  Rivales
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {!rivals.length?(
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      py:3,
                      color:colors.textSecondary
                    }}
                  >
                    No hay rivales asignados.
                  </TableCell>
                </TableRow>
              ):(
                rivals.map(team=>(
                  <TableRow key={team.slug}>
                    <TableCell>
                      <Box
                        sx={{
                          display:"flex",
                          alignItems:"center",
                          gap:1
                        }}
                      >
                        {team.logo&&(
                          <Box
                            component="img"
                            src={team.logo}
                            alt={team.name}
                            sx={{
                              width:30,
                              height:30,
                              objectFit:"contain",
                              flexShrink:0
                            }}
                          />
                        )}

                        <Typography
                          sx={{
                            fontSize:typography.sizes.sm,
                            fontWeight:600,
                            color:colors.text
                          }}
                        >
                          {team.name}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {phase.group&&(
        <Typography
          sx={{
            mt:0.7,
            fontSize:typography.sizes.xs,
            color:colors.textSecondary
          }}
        >
          Clasifican {phase.group.teams_advance??"—"} equipo{phase.group.teams_advance===1?"":"s"} de este grupo.
        </Typography>
      )}
    </Box>
  );
}